import * as signalr from '@microsoft/signalr';
import { action, observable } from 'mobx';

const logLevel =
  process.env.NODE_ENV === 'production'
    ? signalr.LogLevel.None
    : signalr.LogLevel.Information;

// A stream that answers `notFound` is answering about a specific sid. Retrying
// is only worth it for the short window where the sale has not propagated yet;
// past that the sid is simply not a sale and the loop has to end.
export const MAX_NOT_FOUND_RETRIES = 10;
export const NOT_FOUND_RETRY_DELAY = 500;

// Transport-level failures get a bounded, jittered backoff instead of a flat
// interval, so a fleet of stale tabs cannot turn into a thundering herd.
export const MAX_CONNECT_ATTEMPTS = 10;

// A permissions miss is re-checked a bounded number of times (whitelisting can
// land moments after the sale payload) and then becomes terminal.
export const MAX_ACCESS_RETRIES = 10;
export const ACCESS_RETRY_DELAY = 500;

const CONNECT_RETRY_DELAY = 5000;
const MAX_CONNECT_RETRY_DELAY = 30000;

function backoffDelay(attempt) {
  const delay = Math.min(
    CONNECT_RETRY_DELAY * Math.pow(2, attempt),
    MAX_CONNECT_RETRY_DELAY
  );
  return delay + Math.floor(Math.random() * 1000);
}

class BoundedRetryPolicy {
  nextRetryDelayInMilliseconds(retryContext) {
    if (retryContext.previousRetryCount >= MAX_CONNECT_ATTEMPTS) {
      console.error(`[ws] automatic reconnect exhausted, giving up`);
      return null;
    }
    return backoffDelay(retryContext.previousRetryCount);
  }
}

class WebSocketStore {
  @observable connected = false;
  @observable forbidden = false;
  @observable notFound = false;
  @observable unavailable = false;

  // Retry budgets are keyed to the target uri, not to the connection: a
  // reconnect against the same sid must not replenish them, or the loop is
  // unbounded again.
  notFoundRetries = 0;
  accessRetries = 0;
  connectAttempts = 0;

  stopped = false;
  retryTimer = null;

  constructor(user) {
    this.user = user;
  }

  hasValidToken() {
    if (!this.user) {
      return true;
    }
    return (
      !!this.user.accessToken && !(this.user.data && this.user.data.expired)
    );
  }

  clearRetryTimer() {
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
  }

  scheduleRetry(delay, callback) {
    this.clearRetryTimer();
    this.retryTimer = setTimeout(() => {
      this.retryTimer = null;
      if (this.stopped) {
        return;
      }
      callback();
    }, delay);
  }

  @action async connect(uri) {
    if (this.connected) {
      return;
    }

    if (uri !== this.uri) {
      this.notFoundRetries = 0;
      this.accessRetries = 0;
    }

    this.forbidden = false;
    this.notFound = false;
    this.unavailable = false;
    this.stopped = false;
    this.connectAttempts = 0;
    this.clearRetryTimer();
    this.uri = uri;

    this.connection = new signalr.HubConnectionBuilder()
      .withUrl(uri, {
        skipNegotiation: true,
        transport: signalr.HttpTransportType.WebSockets,
        accessTokenFactory: () => this.user.accessToken,
      })
      .withAutomaticReconnect(new BoundedRetryPolicy())
      .configureLogging(logLevel)
      .build();

    this.connection.onclose((err) => this.onDisconnect(err));
    this.connection.on('connected', (_) => this.onConnected());
    this.connection.on('update', (data) => this.onUpdate(data));
    this.connection.on('error', (data) => this.onError(data));

    await this.tryConnect();
  }

  disconnect = async () => {
    this.stopped = true;
    this.clearRetryTimer();
    if (!this.connection) {
      return;
    }
    await this.connection.stop();
  };

  reconnect = async () => {
    await this.disconnect();
    await this.connect(this.uri);
  };

  tryConnect = async () => {
    if (this.stopped) {
      return;
    }
    try {
      await this.connection.start();
      this.connectAttempts = 0;
    } catch (error) {
      if (this.stopped) {
        return;
      }
      // A session whose token is gone or expired will be rejected on every
      // attempt; retrying it just keeps a dead tab hammering the service.
      if (!this.hasValidToken()) {
        console.error(`[ws] no usable access token, not retrying`);
        this.onUnavailable();
        return;
      }
      if (this.connectAttempts >= MAX_CONNECT_ATTEMPTS) {
        console.error(`[ws] connect attempts exhausted, giving up`);
        this.onUnavailable();
        return;
      }
      const delay = backoffDelay(this.connectAttempts);
      this.connectAttempts += 1;
      this.scheduleRetry(delay, this.tryConnect);
    }
  };

  onUpdate(data) {
    // Real payload for this sid: the sale exists, so the notFound budget is
    // spent on a genuinely transient condition and can be released.
    this.notFoundRetries = 0;
    this.received(data.payload, data.timestamp);
  }

  onError(err) {
    console.error(err);
    if (err.status === 'notFound') {
      this.onNotFound();
    }
    if (err.status === 'forbidden') {
      this.forbidden = true;
    }
  }

  @action onNotFound() {
    if (this.notFoundRetries >= MAX_NOT_FOUND_RETRIES) {
      console.error(
        `[ws] sale not found after ${this.notFoundRetries} retries`
      );
      this.notFound = true;
      this.disconnect();
      return;
    }
    this.notFoundRetries += 1;
    this.scheduleRetry(NOT_FOUND_RETRY_DELAY, this.reconnect);
  }

  @action onAccessDenied() {
    if (this.accessRetries >= MAX_ACCESS_RETRIES) {
      console.error(`[ws] access denied after ${this.accessRetries} retries`);
      this.forbidden = true;
      this.disconnect();
      return;
    }
    this.accessRetries += 1;
    this.scheduleRetry(ACCESS_RETRY_DELAY, this.reconnect);
  }

  @action onUnavailable() {
    this.unavailable = true;
    this.disconnect();
  }

  @action onConnected() {
    this.connected = true;
    this.connectAttempts = 0;
  }

  @action onDisconnect(err) {
    this.connected = false;
  }

  received = (message) => {};
}

const websocket = new WebSocketStore();

export default websocket;
export { WebSocketStore, BoundedRetryPolicy };



// WEBPACK FOOTER //
// ./src/app/containers/App/stores/WebSocketStore.js